import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import OpacityOutlinedIcon from '@mui/icons-material/OpacityOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useColorMode } from '../ThemeContext';
import { db, appCheckHeaders } from '../firebase';
import { formatPropertyDate } from '../utils/formatDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

type AllocationMethod =
  | 'equal'
  | 'by-unit'
  | '50-50'
  | '100-occupancy'
  | '100-ratio-occupancy'
  | '100-sqft';

const ALLOCATION_LABELS: Record<AllocationMethod, string> = {
  equal: 'Equal (per unit)',
  'by-unit': 'By unit',
  '50-50': '50% Occupancy / 50% Sq Ft',
  '100-occupancy': '100% Occupancy',
  '100-ratio-occupancy': '100% Ratio Occupancy',
  '100-sqft': '100% Square Footage',
};

interface UnitRow {
  id: string;
  unitNumber: string;
  squareFootage: number;
  occupants: number;
  isOwnerOccupied: boolean;
  tenantId: string | null;
}

interface TenantRow {
  id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  shareBasis?: string;
  unitFraction?: number;
  status?: string;
  unitId?: string | null;
}

interface ChargeRow {
  id: string;
  label: string;
  amountCents: number;
  allocationMethod: AllocationMethod;
  appliesTo: 'all' | string[];
  isActive: boolean;
}

interface WaterUsage {
  totalGallons: number;
  totalCost: number;
  date: string;
}

interface UploadResponse {
  success: boolean;
  measurementId: string;
  waterUsage: WaterUsage;
}

interface InvoiceResult {
  type: 'success' | 'warning' | 'already-generated' | 'error';
  message: string;
  failures?: Array<{ tenantId: string; error: string }>;
  warnings?: string[];
  recovery?: string;
}

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success'> = {
  pending: 'warning',
  active: 'success',
  inactive: 'default',
};

const STATUS_ICONS: Record<string, React.ReactElement> = {
  pending: <HourglassEmptyOutlinedIcon sx={{ fontSize: '12px !important' }} />,
  active: <CheckCircleOutlineIcon sx={{ fontSize: '12px !important' }} />,
  inactive: <RemoveCircleOutlineIcon sx={{ fontSize: '12px !important' }} />,
};

function SectionHeader({
  icon,
  title,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          mr: 1.5,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h6"
        sx={{ flexGrow: 1, fontWeight: 600, fontSize: '1rem', letterSpacing: 0 }}
      >
        {title}
      </Typography>
      {action}
    </Box>
  );
}

export default function PropertyDetail() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const { mode, toggleMode } = useColorMode();

  const [propertyName, setPropertyName] = useState<string | null>(null);
  const [propertyAddress, setPropertyAddress] = useState<string | null>(null);
  const [propertyTimeZone, setPropertyTimeZone] = useState<string | null>(null);
  const [allocationMethod, setAllocationMethod] = useState<AllocationMethod>('equal');
  const [propertyLoading, setPropertyLoading] = useState(true);
  const [savingAllocation, setSavingAllocation] = useState(false);

  const [units, setUnits] = useState<UnitRow[] | null>(null);
  const [unitError, setUnitError] = useState<string | null>(null);
  const [addUnitOpen, setAddUnitOpen] = useState(false);
  const [editUnit, setEditUnit] = useState<UnitRow | null>(null);
  const [uNumber, setUNumber] = useState('');
  const [uSqft, setUSqft] = useState('');
  const [uOccupants, setUOccupants] = useState('');
  const [uOwner, setUOwner] = useState(false);
  const [savingUnit, setSavingUnit] = useState(false);
  const [unitFormError, setUnitFormError] = useState<string | null>(null);

  const [charges, setCharges] = useState<ChargeRow[] | null>(null);
  const [chargeError, setChargeError] = useState<string | null>(null);
  const [addChargeOpen, setAddChargeOpen] = useState(false);
  const [editCharge, setEditCharge] = useState<ChargeRow | null>(null);
  const [cLabel, setCLabel] = useState('');
  const [cAmount, setCAmount] = useState('');
  const [cMethod, setCMethod] = useState<AllocationMethod>('equal');
  const [cAppliesTo, setCAppliesTo] = useState<'all' | string[]>('all');
  const [cActive, setCActive] = useState(true);
  const [savingCharge, setSavingCharge] = useState(false);
  const [chargeFormError, setChargeFormError] = useState<string | null>(null);

  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [addTenantOpen, setAddTenantOpen] = useState(false);
  const [tFirstName, setTFirstName] = useState('');
  const [tLastName, setTLastName] = useState('');
  const [tEmail, setTEmail] = useState('');
  const [tShareBasis, setTShareBasis] = useState<'equal' | 'unit'>('equal');
  const [tUnitFraction, setTUnitFraction] = useState('');
  const [tDisplayName, setTDisplayName] = useState('');
  const [tAssignUnit, setTAssignUnit] = useState('');
  const [tNoticeAcknowledged, setTNoticeAcknowledged] = useState(false);
  const [addingTenant, setAddingTenant] = useState(false);
  const [addTenantError, setAddTenantError] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<TenantRow | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  // Send sign-in link state (br-gvt).
  const [sendingLinkId, setSendingLinkId] = useState<string | null>(null);
  const [linkSnackbar, setLinkSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Bulk multi-select state for the tenant list (br-057).
  const [selectedTenantIds, setSelectedTenantIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<{ ok: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [measurement, setMeasurement] = useState<UploadResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [invoiceResult, setInvoiceResult] = useState<InvoiceResult | null>(null);
  const [unresolvedDebits, setUnresolvedDebits] = useState<Array<{ id: string; amount: number }>>([]);
  const [collectionStatusError, setCollectionStatusError] = useState(false);
  const [collectionStatusLoading, setCollectionStatusLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchCollectionStatus = useCallback(async () => {
    if (!user || !propertyId) return;
    setCollectionStatusLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'clients', user.uid, 'properties', propertyId, 'invoices'),
        where('collectionState', '==', 'unresolved'),
      ));
      setUnresolvedDebits(snap.docs.map((row) => ({ id: row.id, amount: row.data().amount })));
      setCollectionStatusError(false);
    } catch {
      setCollectionStatusError(true);
    } finally {
      setCollectionStatusLoading(false);
    }
  }, [user, propertyId]);

  useEffect(() => { void fetchCollectionStatus(); }, [fetchCollectionStatus]);

  useEffect(() => {
    if (!user || !propertyId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'clients', user.uid, 'properties', propertyId));
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data() as { name?: string; address?: string; allocationMethod?: AllocationMethod; timeZone?: string };
          setPropertyName(d.name ?? propertyId);
          setPropertyAddress(d.address ?? null);
          setPropertyTimeZone(d.timeZone ?? null);
          setAllocationMethod(d.allocationMethod ?? 'equal');
        } else {
          setPropertyName(propertyId);
        }
      } catch {
        if (!cancelled) setPropertyName(propertyId);
      } finally {
        if (!cancelled) setPropertyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, propertyId]);

  const fetchUnits = useCallback(async () => {
    if (!user || !propertyId) return;
    setUnitError(null);
    try {
      const snap = await getDocs(
        collection(db, 'clients', user.uid, 'properties', propertyId, 'units'),
      );
      setUnits(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<UnitRow, 'id'>) })));
    } catch (err: any) {
      setUnitError(err?.message ?? 'Failed to load units');
    }
  }, [user, propertyId]);

  useEffect(() => { void fetchUnits(); }, [fetchUnits]);

  const fetchCharges = useCallback(async () => {
    if (!user || !propertyId) return;
    setChargeError(null);
    try {
      const snap = await getDocs(
        collection(db, 'clients', user.uid, 'properties', propertyId, 'recurringCharges'),
      );
      setCharges(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChargeRow, 'id'>) })));
    } catch (err: any) {
      setChargeError(err?.message ?? 'Failed to load recurring charges');
    }
  }, [user, propertyId]);

  useEffect(() => { void fetchCharges(); }, [fetchCharges]);

  const fetchTenants = useCallback(async () => {
    if (!user || !propertyId) return;
    setTenantError(null);
    try {
      const snap = await getDocs(
        collection(db, 'clients', user.uid, 'properties', propertyId, 'tenants'),
      );
      setTenants(snap.docs.map((d) => ({ ...(d.data() as TenantRow), id: d.id })));
    } catch (err: any) {
      setTenantError(err?.message ?? 'Failed to load tenants');
    }
  }, [user, propertyId]);

  useEffect(() => { void fetchTenants(); }, [fetchTenants]);

  const callWithAuth = useCallback(
    async (path: string, init: RequestInit): Promise<Response> => {
      const send = async (forceRefresh: boolean) => {
        const token = await getIdToken(forceRefresh);
        if (!token) throw new Error('Not signed in');
        const headers = new Headers(init.headers);
        headers.set('Authorization', `Bearer ${token}`);
        const ac = await appCheckHeaders();
        for (const [k, v] of Object.entries(ac)) headers.set(k, v);
        return fetch(`${BACKEND_URL}${path}`, { ...init, headers });
      };
      let res = await send(false);
      if (res.status === 401) res = await send(true);
      return res;
    },
    [getIdToken],
  );

  const saveAllocationMethod = async (method: AllocationMethod) => {
    setSavingAllocation(true);
    try {
      const res = await callWithAuth(`/owner/properties/${propertyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allocationMethod: method }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? 'Failed to save');
      }
      setAllocationMethod(method);
    } catch {
      /* non-fatal */
    } finally {
      setSavingAllocation(false);
    }
  };

  const openAddUnit = () => {
    setEditUnit(null);
    setUNumber(''); setUSqft(''); setUOccupants(''); setUOwner(false);
    setUnitFormError(null);
    setAddUnitOpen(true);
  };

  const openEditUnit = (u: UnitRow) => {
    setEditUnit(u);
    setUNumber(u.unitNumber);
    setUSqft(String(u.squareFootage));
    setUOccupants(String(u.occupants));
    setUOwner(u.isOwnerOccupied);
    setUnitFormError(null);
    setAddUnitOpen(true);
  };

  const handleSaveUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    const sqft = parseInt(uSqft, 10);
    const occ = parseInt(uOccupants, 10);
    if (!uNumber.trim()) { setUnitFormError('Unit number is required'); return; }
    if (isNaN(sqft) || sqft <= 0) { setUnitFormError('Square footage must be a positive integer'); return; }
    if (isNaN(occ) || occ < 1) { setUnitFormError('Occupants must be at least 1'); return; }
    setSavingUnit(true);
    setUnitFormError(null);
    try {
      const payload = { unitNumber: uNumber.trim(), squareFootage: sqft, occupants: occ, isOwnerOccupied: uOwner };
      const res = editUnit
        ? await callWithAuth(`/properties/${propertyId}/units/${editUnit.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : await callWithAuth(`/properties/${propertyId}/units`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error ?? `Error ${res.status}`);
      setAddUnitOpen(false);
      await fetchUnits();
    } catch (err: any) {
      setUnitFormError(err?.message ?? 'Failed to save unit');
    } finally {
      setSavingUnit(false);
    }
  };

  const handleDeleteUnit = async (unitId: string) => {
    try {
      const res = await callWithAuth(`/properties/${propertyId}/units/${unitId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setUnitError(body?.error ?? 'Failed to delete unit');
        return;
      }
      await fetchUnits();
    } catch (err: any) {
      setUnitError(err?.message ?? 'Failed to delete unit');
    }
  };

  const openAddCharge = () => {
    setEditCharge(null);
    setCLabel(''); setCAmount(''); setCMethod('equal'); setCAppliesTo('all'); setCActive(true);
    setChargeFormError(null);
    setAddChargeOpen(true);
  };

  const openEditCharge = (c: ChargeRow) => {
    setEditCharge(c);
    setCLabel(c.label);
    setCAmount(String((c.amountCents / 100).toFixed(2)));
    setCMethod(c.allocationMethod);
    setCAppliesTo(c.appliesTo);
    setCActive(c.isActive);
    setChargeFormError(null);
    setAddChargeOpen(true);
  };

  const handleSaveCharge = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountDollars = parseFloat(cAmount);
    if (!cLabel.trim()) { setChargeFormError('Label is required'); return; }
    if (isNaN(amountDollars) || amountDollars <= 0) { setChargeFormError('Amount must be positive'); return; }
    const amountCents = Math.round(amountDollars * 100);
    setSavingCharge(true);
    setChargeFormError(null);
    try {
      const payload = { label: cLabel.trim(), amountCents, allocationMethod: cMethod, appliesTo: cAppliesTo, isActive: cActive };
      const res = editCharge
        ? await callWithAuth(`/properties/${propertyId}/recurringCharges/${editCharge.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : await callWithAuth(`/properties/${propertyId}/recurringCharges`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error ?? `Error ${res.status}`);
      setAddChargeOpen(false);
      await fetchCharges();
    } catch (err: any) {
      setChargeFormError(err?.message ?? 'Failed to save charge');
    } finally {
      setSavingCharge(false);
    }
  };

  const handleDeleteCharge = async (chargeId: string) => {
    try {
      const res = await callWithAuth(`/properties/${propertyId}/recurringCharges/${chargeId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setChargeError(body?.error ?? 'Failed to delete charge');
        return;
      }
      await fetchCharges();
    } catch (err: any) {
      setChargeError(err?.message ?? 'Failed to delete charge');
    }
  };

  const openAddTenant = () => {
    setTFirstName(''); setTLastName(''); setTEmail('');
    setTShareBasis('equal'); setTUnitFraction(''); setTDisplayName(''); setTAssignUnit('');
    setTNoticeAcknowledged(false);
    setAddTenantError(null);
    setAddTenantOpen(true);
  };

  const handleAddTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    const firstName = tFirstName.trim();
    const lastName = tLastName.trim();
    const email = tEmail.trim();
    if (!firstName) { setAddTenantError('First name is required'); return; }
    if (!lastName) { setAddTenantError('Last name is required'); return; }
    if (!email) { setAddTenantError('Email is required'); return; }
    if (tShareBasis === 'unit') {
      const frac = parseFloat(tUnitFraction);
      if (isNaN(frac) || frac <= 0 || frac > 1) {
        setAddTenantError('Unit fraction must be between 0 (exclusive) and 1 (inclusive)');
        return;
      }
    }
    setAddingTenant(true);
    setAddTenantError(null);
    try {
      const body: Record<string, unknown> = { firstName, lastName, email, shareBasis: tShareBasis };
      if (tShareBasis === 'unit') body.unitFraction = parseFloat(tUnitFraction);
      if (tDisplayName.trim()) body.displayName = tDisplayName.trim();
      if (tAssignUnit) body.unitId = tAssignUnit;
      if (tNoticeAcknowledged) body.preTenancyNoticeAcknowledged = true;
      const res = await callWithAuth(`/properties/${propertyId}/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const resBody = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(resBody?.error ?? `Error ${res.status}`);
      setAddTenantOpen(false);
      await Promise.all([fetchTenants(), fetchUnits()]);
    } catch (err: any) {
      setAddTenantError(err?.message ?? 'Failed to add tenant');
    } finally {
      setAddingTenant(false);
    }
  };

  // br-gvt: (re)send passwordless sign-in link to a tenant.
  const handleSendSignInLink = async (tenant: TenantRow) => {
    if (!propertyId) return;
    setSendingLinkId(tenant.id);
    try {
      const res = await callWithAuth(
        `/owner/properties/${propertyId}/tenants/${tenant.id}/send-signin-link`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `Request failed (${res.status})`);
      }
      const email = tenant.email ?? 'tenant';
      setLinkSnackbar({ message: `Sign-in link sent to ${email}`, severity: 'success' });
    } catch (err: any) {
      setLinkSnackbar({ message: err?.message ?? 'Failed to send sign-in link', severity: 'error' });
    } finally {
      setSendingLinkId(null);
    }
  };

  const handleDeactivateTenant = async () => {
    if (!deactivateTarget || !propertyId) return;
    setDeactivating(true);
    setDeactivateError(null);
    try {
      const res = await callWithAuth(
        `/properties/${propertyId}/tenants/${deactivateTarget.id}`,
        { method: 'DELETE' },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error ?? `Error ${res.status}`);
      setDeactivateTarget(null);
      await Promise.all([fetchTenants(), fetchUnits()]);
    } catch (err: any) {
      setDeactivateError(err?.message ?? 'Failed to deactivate tenant');
    } finally {
      setDeactivating(false);
    }
  };

  // --- Bulk actions on selected tenants (br-057) ---
  // Only tenants that can actually be deactivated (status !== 'inactive') are
  // selectable, mirroring the per-row Deactivate action's visibility rule.
  const selectableTenants = (tenants ?? []).filter((t) => t.status !== 'inactive');

  const toggleTenantSelected = (id: string) => {
    setBulkSummary(null);
    setBulkError(null);
    setSelectedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllTenants = () => {
    setBulkSummary(null);
    setBulkError(null);
    setSelectedTenantIds((prev) => {
      const allSelected = selectableTenants.length > 0 && selectableTenants.every((t) => prev.has(t.id));
      return allSelected ? new Set() : new Set(selectableTenants.map((t) => t.id));
    });
  };

  const clearTenantSelection = () => {
    setSelectedTenantIds(new Set());
    setBulkSummary(null);
    setBulkError(null);
  };

  // Bulk Deactivate: loops the EXACT existing per-tenant endpoint
  // (DELETE /properties/:propertyId/tenants/:id) once per selected tenant.
  const handleBulkDeactivate = async () => {
    if (!propertyId) return;
    const ids = selectableTenants.filter((t) => selectedTenantIds.has(t.id)).map((t) => t.id);
    if (ids.length === 0) return;
    setBulkRunning(true);
    setBulkError(null);
    setBulkSummary(null);
    let ok = 0;
    for (const id of ids) {
      try {
        const res = await callWithAuth(`/properties/${propertyId}/tenants/${id}`, { method: 'DELETE' });
        if (res.ok) ok += 1;
      } catch {
        // Count as failure; summary reports ok/total.
      }
    }
    setBulkSummary({ ok, total: ids.length });
    if (ok < ids.length) {
      setBulkError(`${ids.length - ok} of ${ids.length} could not be deactivated.`);
    }
    setSelectedTenantIds(new Set());
    setBulkRunning(false);
    await Promise.all([fetchTenants(), fetchUnits()]);
  };

  const uploadFile = useCallback(
    async (file: File) => {
      if (!propertyId) return;
      if (file.type !== 'application/pdf') {
        setUploadError('Please upload a PDF file.');
        return;
      }
      setUploading(true);
      setUploadError(null);
      setMeasurement(null);
      setInvoiceResult(null);
      try {
        const formData = new FormData();
        formData.append('pdf', file);
        formData.append('propertyId', propertyId);
        const res = await callWithAuth('/upload-bill', { method: 'POST', body: formData });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error ?? `Upload failed (${res.status})`);
        }
        const data = (await res.json()) as UploadResponse;
        setMeasurement(data);
      } catch (err: any) {
        setUploadError(err?.message ?? 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [propertyId, callWithAuth],
  );

  const generateInvoices = async () => {
    if (!propertyId) return;
    setGenerating(true);
    setInvoiceResult(null);
    setUploadError(null);
    try {
      const res = await callWithAuth('/generate-invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ propertyId, measurementId: measurement?.measurementId }),
      });
      const body = await res.json();
      const failures: InvoiceResult['failures'] = body.failures ?? [];
      const warnings: string[] = [...(body.warnings ?? [])];
      if (body.sourceBillMissing && !warnings.includes('source_bill_missing')) {
        warnings.push('source_bill_missing');
      }
      const recovery = 'Review the existing invoices and contact support to reconcile payment outcomes before taking further billing action. Do not regenerate this bill: a reported failure may still have initiated a charge.';
      if (res.status === 409) {
        setInvoiceResult({
          type: 'already-generated', message: body?.error ?? 'Invoices already generated.',
          failures, warnings,
          recovery: 'Review the existing invoices for this bill. Do not regenerate it or upload it again to bypass duplicate protection.',
        });
        return;
      }
      if (!res.ok) {
        setInvoiceResult({
          type: 'error',
          message: body?.error ?? (res.status === 502
            ? `Payment initiation failed for all reported tenants. ${body.invoiceIds?.length ?? 0} invoice records were created; payment outcomes need review.`
            : `Generate failed (${res.status})`),
          failures, warnings,
          recovery: res.status === 422 ? 'Resolve the reported billing issue before generating invoices.' : recovery,
        });
        return;
      }
      const ids: string[] | undefined = body.invoiceIds ?? (body.invoiceId ? [body.invoiceId] : undefined);
      const incomplete = res.status === 207 || body.success === false || (failures?.length ?? 0) > 0;
      setInvoiceResult({
        type: incomplete || warnings.length > 0 ? 'warning' : 'success',
        message: incomplete
          ? `Billing is incomplete. ${ids?.length ?? 0} invoice records were created; payment initiation needs review.`
          : ids?.length
            ? `Generated ${ids.length} invoice${ids.length === 1 ? '' : 's'}.`
            : 'Invoices generated.',
        failures, warnings,
        recovery: incomplete ? recovery : undefined,
      });
    } catch (err: any) {
      setInvoiceResult({
        type: 'error', message: err?.message ?? 'Generate failed',
        recovery: 'The billing outcome is unknown. Review existing invoices and contact support to reconcile charges before trying again. Do not regenerate this bill.',
      });
    } finally {
      setGenerating(false);
      void fetchCollectionStatus();
    }
  };

  const vacantUnits = (units ?? []).filter((u) => !u.isOwnerOccupied && !u.tenantId);

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Tooltip title="Go back">
            <IconButton
              color="inherit"
              onClick={() => navigate('/')}
              edge="start"
              aria-label="Go back"
              sx={{ mr: 1, opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <ArrowBackOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: '-0.5px' }}
          >
            Utilibill
          </Typography>
          {user?.email && (
            <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.8rem', mr: 0.5 }}>
              {user.email}
            </Typography>
          )}
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              color="inherit"
              onClick={toggleMode}
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, pb: 8 }}>
        {/* Property header */}
        {propertyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="h4"
                sx={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
              >
                {propertyName}
              </Typography>
              {propertyAddress && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.75, color: 'text.secondary' }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2">{propertyAddress}</Typography>
                </Box>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => navigate(`/properties/${propertyId}/history`)}
              sx={{ flexShrink: 0 }}
            >
              Billing history
            </Button>
          </Box>
        )}

        {/* Allocation method */}
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="h6">Payment reconciliation</Typography>
          <Button size="small" disabled={collectionStatusLoading} onClick={() => void fetchCollectionStatus()}>
            Refresh payment status
          </Button>
          {collectionStatusError ? (
            <Alert severity="warning">Payment status could not be checked. Refresh before taking further billing action.</Alert>
          ) : unresolvedDebits.length > 0 ? (
            <Alert severity="warning">
              Payment outcome unresolved. Do not collect these invoices again or regenerate their bills.
              Automatic reconciliation will look for the existing debit; contact support if this persists.
              <ul>
                {unresolvedDebits.map((invoice) => (
                  <li key={invoice.id}>Invoice {invoice.id}: ${(invoice.amount / 100).toFixed(2)}</li>
                ))}
              </ul>
            </Alert>
          ) : !collectionStatusLoading && (
            <Typography variant="body2">No unresolved initial debits.</Typography>
          )}
        </Paper>
        <Paper
          sx={{
            p: 2.5,
            mb: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.72rem' }}>
            RUBS Allocation Method
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Select
              size="small"
              value={allocationMethod}
              onChange={(e) => void saveAllocationMethod(e.target.value as AllocationMethod)}
              disabled={savingAllocation}
              sx={{ minWidth: { xs: 0, sm: 280 }, flexGrow: { xs: 1, sm: 0 }, maxWidth: '100%' }}
            >
              {(Object.keys(ALLOCATION_LABELS) as AllocationMethod[]).map((m) => (
                <MenuItem key={m} value={m}>{ALLOCATION_LABELS[m]}</MenuItem>
              ))}
            </Select>
            {savingAllocation && <CircularProgress size={16} />}
          </Stack>
          <FormHelperText sx={{ mt: 0.5 }}>How the water bill is split across billable units.</FormHelperText>
        </Paper>

        {/* Upload zone */}
        <Paper
          role="button"
          tabIndex={0}
          aria-label="Upload water bill PDF"
          sx={{
            p: 5,
            mb: 3,
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 2,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: dragOver ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(37, 99, 235, 0.03)' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void uploadFile(f);
          }}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); }
          }}
        >
          <CloudUploadOutlinedIcon
            sx={{
              fontSize: 48,
              color: dragOver ? 'primary.main' : 'text.secondary',
              mb: 1.5,
              transition: 'color 0.2s',
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Drop your water-bill PDF here
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            or click to browse
          </Typography>
          <Button variant="contained" component="span" size="small">
            Choose PDF
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadFile(f);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
        </Paper>

        {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}

        {/* Measurement result */}
        {measurement && (
          <Paper
            sx={{
              mb: 3,
              border: '1px solid',
              borderColor: 'success.light',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark', mb: 2, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Bill parsed successfully
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <OpacityOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Usage</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                        {measurement.waterUsage.totalGallons.toLocaleString()} gal
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AttachMoneyOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Cost</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                        ${measurement.waterUsage.totalCost.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarTodayOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Billing date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatPropertyDate(measurement.waterUsage.date, propertyTimeZone)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.default' }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => void generateInvoices()}
                disabled={generating || invoiceResult !== null}
              >
                {generating ? 'Generating…' : 'Generate invoices'}
              </Button>
              <Button
                size="small"
                onClick={() => { setMeasurement(null); setInvoiceResult(null); setUploadError(null); }}
                disabled={generating}
                sx={{ color: 'text.secondary' }}
              >
                Discard
              </Button>
              {generating && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Box>
          </Paper>
        )}

        {invoiceResult && (
          <Alert
            severity={invoiceResult.type === 'already-generated' ? 'info' : invoiceResult.type}
            sx={{ mb: 3 }}
          >
            {invoiceResult.message}
            {(invoiceResult.failures?.length ?? 0) > 0 && (
              <Box component="ul" sx={{ my: 1, pl: 2 }}>
                {invoiceResult.failures!.map((failure, index) => {
                  const tenant = tenants?.find((t) => t.id === failure.tenantId);
                  const name = tenant?.displayName || [tenant?.firstName, tenant?.lastName].filter(Boolean).join(' ');
                  return (
                    <li key={`${failure.tenantId}-${index}`}>
                      {name ? `${name} (${failure.tenantId})` : `Tenant ${failure.tenantId}`}: Payment initiation needs review. Contact support to reconcile the payment outcome.
                    </li>
                  );
                })}
              </Box>
            )}
            {invoiceResult.warnings?.map((warning, index) => (
              <Typography key={`${warning}-${index}`} variant="body2" sx={{ mt: 1 }}>
                {warning === 'source_bill_missing'
                  ? 'Source bill missing (source_bill_missing): the original water bill PDF is unavailable to tenants. Keep a copy and contact support to restore access to the source bill.'
                  : `Warning: ${warning}`}
              </Typography>
            ))}
            {invoiceResult.recovery && <Typography variant="body2" sx={{ mt: 1 }}>{invoiceResult.recovery}</Typography>}
          </Alert>
        )}

        {/* Units */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            icon={<ApartmentOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Units"
            action={
              <Button variant="outlined" size="small" onClick={openAddUnit}>
                Add unit
              </Button>
            }
          />

          {unitError && <Alert severity="error" sx={{ mb: 2 }}>{unitError}</Alert>}

          {units === null && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {units !== null && units.length === 0 && (
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'transparent',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No units yet. Add units to enable RUBS billing.
              </Typography>
            </Paper>
          )}

          {units !== null && units.length > 0 && (
            <Grid container spacing={1.5}>
              {units.map((u) => {
                const linkedTenant = tenants?.find((t) => t.id === u.tenantId);
                const tenantLabel = linkedTenant
                  ? linkedTenant.displayName ?? `${linkedTenant.firstName ?? ''} ${linkedTenant.lastName ?? ''}`.trim()
                  : null;
                return (
                  <Grid item xs={12} sm={6} key={u.id}>
                    <Paper
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: u.isOwnerOccupied ? 'rgba(11,25,41,0.08)' : 'rgba(37,99,235,0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: u.isOwnerOccupied ? 'text.secondary' : 'primary.main',
                          }}
                        >
                          {u.unitNumber}
                        </Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Unit {u.unitNumber}
                          </Typography>
                          {u.isOwnerOccupied && (
                            <Chip label="Owner" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {u.squareFootage} sq ft · {u.occupants} occ · {tenantLabel ?? 'Vacant'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0} flexShrink={0}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditUnit(u)} aria-label="Edit unit">
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => void handleDeleteUnit(u.id)} aria-label="Delete unit">
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* Recurring charges */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            icon={<ReceiptOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Recurring charges"
            action={
              <Button variant="outlined" size="small" onClick={openAddCharge}>
                Add charge
              </Button>
            }
          />

          {chargeError && <Alert severity="error" sx={{ mb: 2 }}>{chargeError}</Alert>}

          {charges === null && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {charges !== null && charges.length === 0 && (
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'transparent',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No recurring charges yet.
              </Typography>
            </Paper>
          )}

          {charges !== null && charges.length > 0 && (
            <Stack spacing={1}>
              {charges.map((c) => (
                <Paper
                  key={c.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    opacity: c.isActive ? 1 : 0.55,
                  }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.label}
                      </Typography>
                      {!c.isActive && <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {ALLOCATION_LABELS[c.allocationMethod]} · {c.appliesTo === 'all' ? 'All units' : `${(c.appliesTo as string[]).length} unit(s)`}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'primary.main', flexShrink: 0 }}
                  >
                    ${(c.amountCents / 100).toFixed(2)}/mo
                  </Typography>
                  <Stack direction="row" spacing={0} flexShrink={0}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditCharge(c)} aria-label="Edit charge">
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => void handleDeleteCharge(c.id)} aria-label="Delete charge">
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>

        {/* Tenants */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            icon={<PeopleOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Tenants"
            action={
              <Button variant="outlined" size="small" onClick={openAddTenant}>
                Add tenant
              </Button>
            }
          />

          {tenantError && <Alert severity="error" sx={{ mb: 2 }}>{tenantError}</Alert>}

          {tenants === null && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {tenants !== null && tenants.length === 0 && (
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'transparent',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No tenants yet. Add one to start generating invoices.
              </Typography>
            </Paper>
          )}

          {tenants !== null && tenants.length > 0 && (
            <Stack spacing={1}>
              {/* Bulk multi-select header + toolbar (br-057) */}
              {selectableTenants.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    rowGap: 1,
                    gap: 1,
                    px: 1,
                    minHeight: 36,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={
                      selectableTenants.length > 0 &&
                      selectableTenants.every((t) => selectedTenantIds.has(t.id))
                    }
                    indeterminate={
                      selectedTenantIds.size > 0 &&
                      !selectableTenants.every((t) => selectedTenantIds.has(t.id))
                    }
                    onChange={toggleSelectAllTenants}
                    disabled={bulkRunning}
                    inputProps={{ 'aria-label': 'Select all tenants' }}
                    sx={{ p: 0.5 }}
                  />
                  {selectedTenantIds.size > 0 ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {selectedTenantIds.size} selected
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<PersonOffOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => void handleBulkDeactivate()}
                        disabled={bulkRunning}
                      >
                        {bulkRunning ? 'Deactivating…' : 'Deactivate selected'}
                      </Button>
                      <Button size="small" onClick={clearTenantSelection} disabled={bulkRunning}>
                        Clear
                      </Button>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Select tenants for bulk actions
                    </Typography>
                  )}
                </Box>
              )}
              {bulkRunning && <LinearProgress />}
              {bulkSummary && (
                <Alert severity={bulkSummary.ok === bulkSummary.total ? 'success' : 'warning'}>
                  {bulkSummary.ok} of {bulkSummary.total} tenants deactivated.
                  {bulkError ? ` ${bulkError}` : ''}
                </Alert>
              )}
              {tenants.map((t) => {
                const displayName = t.displayName ?? `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim();
                const initials = `${(t.firstName ?? '').charAt(0)}${(t.lastName ?? '').charAt(0)}`.toUpperCase() || '?';
                const assignedUnit = units?.find((u) => u.id === (t.unitId ?? undefined));
                return (
                  <Paper
                    key={t.id}
                    sx={{
                      px: 2,
                      py: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    {t.status !== 'inactive' && (
                      <Checkbox
                        size="small"
                        checked={selectedTenantIds.has(t.id)}
                        onChange={() => toggleTenantSelected(t.id)}
                        disabled={bulkRunning}
                        inputProps={{ 'aria-label': `Select ${displayName}` }}
                        sx={{ p: 0.5, flexShrink: 0 }}
                      />
                    )}
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'primary.main',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {t.email}{assignedUnit ? ` · Unit ${assignedUnit.unitNumber}` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      icon={STATUS_ICONS[t.status ?? 'pending']}
                      label={t.status ?? 'pending'}
                      color={STATUS_COLORS[t.status ?? 'pending'] ?? 'default'}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                    />
                    {t.email && t.status !== 'inactive' && (
                      <Tooltip title={t.status === 'pending' ? 'Send sign-in link' : 'Resend sign-in link'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => void handleSendSignInLink(t)}
                            aria-label={t.status === 'pending' ? 'Send sign-in link' : 'Resend sign-in link'}
                            disabled={sendingLinkId === t.id}
                            sx={{ flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                          >
                            {sendingLinkId === t.id
                              ? <CircularProgress size={14} />
                              : <MailOutlineIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {t.status !== 'inactive' && (
                      <Tooltip title="Deactivate tenant">
                        <IconButton
                          size="small"
                          onClick={() => { setDeactivateError(null); setDeactivateTarget(t); }}
                          aria-label="Deactivate tenant"
                          sx={{ flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                        >
                          <PersonOffOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>
      </Container>

      {/* Add/edit unit dialog */}
      <Dialog open={addUnitOpen} onClose={() => setAddUnitOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={(e) => void handleSaveUnit(e)}>
          <DialogTitle>{editUnit ? 'Edit unit' : 'Add unit'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Unit number"
                value={uNumber}
                onChange={(e) => setUNumber(e.target.value)}
                required
                fullWidth
                autoFocus
                placeholder="e.g. 1A, 2, Ground"
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Square footage"
                  type="number"
                  value={uSqft}
                  onChange={(e) => setUSqft(e.target.value)}
                  required
                  fullWidth
                  inputProps={{ min: 1, step: 1 }}
                />
                <TextField
                  label="Occupants"
                  type="number"
                  value={uOccupants}
                  onChange={(e) => setUOccupants(e.target.value)}
                  required
                  fullWidth
                  inputProps={{ min: 1, step: 1 }}
                />
              </Stack>
              <FormControlLabel
                control={<Switch checked={uOwner} onChange={(e) => setUOwner(e.target.checked)} />}
                label="Owner-occupied (excluded from billing)"
              />
              {unitFormError && <Alert severity="error">{unitFormError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddUnitOpen(false)} disabled={savingUnit}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingUnit}>
              {savingUnit ? 'Saving…' : editUnit ? 'Save' : 'Add unit'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Add/edit recurring charge dialog */}
      <Dialog open={addChargeOpen} onClose={() => setAddChargeOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={(e) => void handleSaveCharge(e)}>
          <DialogTitle>{editCharge ? 'Edit charge' : 'Add recurring charge'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Label"
                value={cLabel}
                onChange={(e) => setCLabel(e.target.value)}
                required
                fullWidth
                autoFocus
                placeholder="e.g. Trash, Pest Control"
              />
              <TextField
                label="Monthly amount ($)"
                type="number"
                value={cAmount}
                onChange={(e) => setCAmount(e.target.value)}
                required
                fullWidth
                inputProps={{ min: 0.01, step: 0.01 }}
              />
              <Select
                value={cMethod}
                onChange={(e) => setCMethod(e.target.value as AllocationMethod)}
                fullWidth
                displayEmpty
              >
                {(Object.keys(ALLOCATION_LABELS) as AllocationMethod[]).map((m) => (
                  <MenuItem key={m} value={m}>{ALLOCATION_LABELS[m]}</MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ mt: -1 }}>How this charge is split across applicable units.</FormHelperText>

              <Select
                value={cAppliesTo === 'all' ? 'all' : 'specific'}
                onChange={(e) => {
                  if (e.target.value === 'all') setCAppliesTo('all');
                  else setCAppliesTo([]);
                }}
                fullWidth
              >
                <MenuItem value="all">All billable units</MenuItem>
                <MenuItem value="specific">Specific units</MenuItem>
              </Select>

              {cAppliesTo !== 'all' && (units ?? []).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Select applicable units:
                  </Typography>
                  {(units ?? []).filter((u) => !u.isOwnerOccupied).map((u) => (
                    <FormControlLabel
                      key={u.id}
                      control={
                        <Switch
                          size="small"
                          checked={Array.isArray(cAppliesTo) && cAppliesTo.includes(u.id)}
                          onChange={(e) => {
                            const cur = Array.isArray(cAppliesTo) ? cAppliesTo : [];
                            setCAppliesTo(
                              e.target.checked ? [...cur, u.id] : cur.filter((id) => id !== u.id),
                            );
                          }}
                        />
                      }
                      label={`Unit ${u.unitNumber}`}
                    />
                  ))}
                </Box>
              )}

              {editCharge && (
                <FormControlLabel
                  control={<Switch checked={cActive} onChange={(e) => setCActive(e.target.checked)} />}
                  label="Active"
                />
              )}

              {chargeFormError && <Alert severity="error">{chargeFormError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddChargeOpen(false)} disabled={savingCharge}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingCharge}>
              {savingCharge ? 'Saving…' : editCharge ? 'Save' : 'Add charge'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Add tenant dialog */}
      <Dialog open={addTenantOpen} onClose={() => setAddTenantOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={(e) => void handleAddTenant(e)}>
          <DialogTitle>Add tenant</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="First name"
                  value={tFirstName}
                  onChange={(e) => setTFirstName(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="Last name"
                  value={tLastName}
                  onChange={(e) => setTLastName(e.target.value)}
                  required
                  fullWidth
                />
              </Stack>
              <TextField
                label="Email"
                type="email"
                value={tEmail}
                onChange={(e) => setTEmail(e.target.value)}
                required
                fullWidth
              />

              {vacantUnits.length > 0 && (
                <Select
                  value={tAssignUnit}
                  onChange={(e) => setTAssignUnit(e.target.value)}
                  fullWidth
                  displayEmpty
                >
                  <MenuItem value="">No unit assigned</MenuItem>
                  {vacantUnits.map((u) => (
                    <MenuItem key={u.id} value={u.id}>Unit {u.unitNumber}</MenuItem>
                  ))}
                </Select>
              )}

              <Select
                value={tShareBasis}
                onChange={(e) => setTShareBasis(e.target.value as 'equal' | 'unit')}
                fullWidth
                displayEmpty
              >
                <MenuItem value="equal">Equal share</MenuItem>
                <MenuItem value="unit">Unit fraction</MenuItem>
              </Select>

              {tShareBasis === 'unit' && (
                <TextField
                  label="Unit fraction (0–1)"
                  type="number"
                  value={tUnitFraction}
                  onChange={(e) => setTUnitFraction(e.target.value)}
                  required
                  fullWidth
                  inputProps={{ min: 0.01, max: 1, step: 0.01 }}
                  helperText="e.g. 0.5 for 50% of the bill"
                />
              )}

              <TextField
                label="Display name (optional)"
                value={tDisplayName}
                onChange={(e) => setTDisplayName(e.target.value)}
                fullWidth
                helperText="Shown on invoices instead of first + last name"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={tNoticeAcknowledged}
                    onChange={(e) => setTNoticeAcknowledged(e.target.checked)}
                    disabled={addingTenant}
                    inputProps={{ 'aria-describedby': 'pre-tenancy-notice-help' }}
                  />
                }
                label="I confirm that I provided this tenant the required written utility billing notice before the tenancy began."
              />
              <FormHelperText id="pre-tenancy-notice-help">
                Select only if you already provided the notice. This records your acknowledgement; it does not send or deliver a notice.
              </FormHelperText>

              {addTenantError && <Alert severity="error">{addTenantError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddTenantOpen(false)} disabled={addingTenant}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={addingTenant}>
              {addingTenant ? 'Adding…' : 'Add tenant'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Deactivate tenant confirmation dialog */}
      <Dialog
        open={deactivateTarget !== null}
        onClose={() => !deactivating && setDeactivateTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Deactivate tenant?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {deactivateTarget
              ? `${deactivateTarget.displayName ?? `${deactivateTarget.firstName ?? ''} ${deactivateTarget.lastName ?? ''}`.trim()} will be marked inactive, removed from billing, and their linked unit will be freed.`
              : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This cannot be undone. The tenant will no longer be able to access the portal.
          </Typography>
          {deactivateError && <Alert severity="error" sx={{ mt: 2 }}>{deactivateError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={deactivating}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deactivating}
            onClick={() => void handleDeactivateTenant()}
          >
            {deactivating ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send sign-in link feedback (br-gvt) */}
      <Snackbar
        open={linkSnackbar !== null}
        autoHideDuration={5000}
        onClose={() => setLinkSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setLinkSnackbar(null)}
          severity={linkSnackbar?.severity ?? 'success'}
          sx={{ width: '100%' }}
        >
          {linkSnackbar?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
